import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Grid,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import HomeHeader   from '../../../Components/HomeHeader';
import HomeFooter   from '../../../Components/HomeFooter';
import { StyledDivider as Divider } from '../../../Components/LightDivider';
import API from '../../../API';
import { lightTheme } from '../../../Utils/baseTheme';
import { useQuery } from 'react-query';
import useAlertDialog from '../../../Components/useAlertDialog';
import AlertDialog   from '../../../Components/AlertDialog';
import { CheckCircle, Download, Payment } from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';

/* ------------ Helpers ------------ */
const formattedDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
const addDays       = (iso, days) => {
  if (!iso) return '';
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString();
};
const safeAmount    = (amt) => (typeof amt === 'number' ? amt : 0);

/* =======================================================================
   Public Invoice page – mobile friendly – now shows Amount Paid
======================================================================= */
const InvoicePage = () => {
  const { invoiceId } = useParams();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const isMobile = useMediaQuery('(max-width:600px)');

  /* --------------------- Data fetch --------------------- */
  const {
    data: invoiceData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => API.invoices.fetch(invoiceId),
    refetchOnWindowFocus: false,
    enabled: !isNaN(invoiceId),
  });

  const invoice  = invoiceData?.invoice;
  const user     = invoiceData?.user;
  const company  = invoiceData?.company;
  const payouts  = invoiceData?.payouts || [];

  /* -------------- Error dialog (fetch) -------------- */
  useEffect(() => {
    if (!error) return;
    openDialog(
      'Error',
      <>
        Error fetching invoice:<br />
        {error?.response?.data?.error || error.message}
      </>,
      closeDialog,
      closeDialog,
      'Ok',
      null
    );
  }, [error, openDialog, closeDialog]);

  /* ---------------- PDF download ---------------- */
  const handleDownloadPDF = async () => {
    const content = document.getElementById('invoice-content');
    const originalWidth = content.style.width;
    content.style.width = '210mm';               // A4 width

    const canvas = await html2canvas(content, {
      scale: 2,
      useCORS: true,
      logging: false,
      allowTaint: true,
      onClone: (doc) => {
        const toHide = [
          doc.getElementById('download-pdf-button'),
          doc.querySelector('button[href]'),
        ];
        toHide.forEach((el) => el && (el.style.display = 'none'));
      },
    });

    content.style.width = originalWidth;

    const img    = canvas.toDataURL('image/jpeg', 0.9);
    const pdf    = new jsPDF('p', 'pt', 'a4');
    const pageW  = pdf.internal.pageSize.getWidth();
    const pageH  = pdf.internal.pageSize.getHeight();
    const ratio  = canvas.height / canvas.width;
    let dispW    = pageW;
    let dispH    = dispW * ratio;
    if (dispH > pageH) {
      dispH = pageH;
      dispW = dispH / ratio;
    }
    pdf.addImage(img, 'JPEG', (pageW - dispW) / 2, (pageH - dispH) / 2, dispW, dispH);
    pdf.save(`invoice_${invoice?.id}.pdf`);
  };

  /* ---------------- States: loading / not-found ---------------- */
  if (isLoading) {
    return (
      <Box sx={{ width:'100vw', height:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <AlertDialog alertState={dialogState} />
        <CircularProgress />
      </Box>
    );
  }
  if (!invoice) {
    return (
      <Box sx={{ width:'100vw', height:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <AlertDialog alertState={dialogState} />
        <Typography>Invoice not found.</Typography>
      </Box>
    );
  }

  /* ---------------- Derived values ---------------- */
  const cd = invoice.custom_details || {};
  const {
    billed_to,
    date_of_issue,
    due_date_override,
    invoice_number: customInvoiceNumber,
    issuing_company,
    amount_paid: cdAmountPaid,              // NEW
  } = cd;

  // header / meta
  const headerCompany   = issuing_company || company?.name || 'Blitz Systems Corp';
  const invoiceNumber   = customInvoiceNumber || invoice.po_number;
  const issueDate       = formattedDate(date_of_issue || invoice.created_at);
  const dueDate         = formattedDate(due_date_override || addDays(invoice.created_at, invoice.payment_term));
  const billedToName    = billed_to || invoice.client_name || (user ? `${user.first_name} ${user.last_name}` : '');
  const billedToEmail   = invoice.client_email || user?.email;

  // money
  const subtotal        = payouts.reduce((acc, p) => acc + safeAmount(p.amount), 0);
  const amountDue       = safeAmount(invoice.amount_due);
  const amountPaid      = safeAmount(cdAmountPaid ?? invoice.amount_paid);  // prioritise custom_details
  const platformFee     = amountDue - subtotal;
  const outstanding     = amountDue - amountPaid;

  /* ---------------- Render ---------------- */
  return (
    <ThemeProvider theme={lightTheme}>
      <AlertDialog alertState={dialogState} />
      <Box sx={{ minHeight:'100vh', background:'linear-gradient(135deg,#f8f9fa 0%,#e9ecef 100%)', display:'flex', flexDirection:'column' }}>
        <HomeHeader />
        <Toolbar />

        <Box sx={{ py:2, flex:1, maxWidth:'100vw', overflowX:'hidden' }}>
          <Button
            disableRipple
            id="download-pdf-button"
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadPDF}
            sx={{
              mb:2,
              ml:{ xs:0, sm:2 },
              width:{ xs:'calc(100% - 32px)', sm:'auto' },
              alignSelf:{ xs:'center', sm:'flex-start' },
              background:'linear-gradient(45deg,#2b2d42 30%,#4a4e69 90%)',
              '&:hover':{ opacity:0.9 },
            }}
          >
            Download PDF
          </Button>

          {/* ============= Invoice body ============= */}
          <Box id="invoice-content" sx={{ mb:4 }}>
            <Paper
              sx={{
                p:{ xs:2, sm:6 },
                width:{ xs:'100%', sm:'210mm' },
                maxWidth:'100%',
                minHeight:{ xs:'auto', sm:'297mm' },
                m:'0 auto',
                fontFamily:'Arial, sans-serif',
                background:'white',
                boxShadow:'none',
                borderRadius:0,
              }}
            >
              {/* -------- Header -------- */}
              <Grid container spacing={4} sx={{ mb:6 }}>
                {/* Left column */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb:4 }}>
                    <Typography variant="h4" sx={{ fontWeight:700, color:'#4a4e69', mb:1 }}>INVOICE</Typography>
                    <Typography variant="h6" sx={{ color:'#4a4e69', fontWeight:400, mb:3 }}>
                      Invoice from {headerCompany}
                    </Typography>
                    {company?.phphref && (
                      <img src={company.phphref} alt={`${company.name} Logo`} style={{ maxWidth:180, maxHeight:80 }} />
                    )}
                  </Box>
                  <Stack spacing={1.5}>
                    <MetaRow label="Date"      value={issueDate} />
                    <MetaRow label="Invoice #" value={invoiceNumber} />
                    <MetaRow label="Due Date"  value={dueDate} />
                  </Stack>
                </Grid>

                {/* Right column */}
                <Grid item xs={12} sm={6} sx={{ textAlign:{ xs:'left', sm:'right' }, mt:{ xs:2, sm:0 } }}>
                  <Paper variant="outlined" sx={{ p:3, background:'#f8f9fa', textAlign:'left' }}>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="textSecondary">Total Amount Due</Typography>
                      <Typography variant="h5" sx={{ fontWeight:'bold' }}>${outstanding.toFixed(2)}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt:1 }}>
                        ({amountPaid > 0 ? `Amount Paid: $${amountPaid.toFixed(2)}` : 'No payment recorded yet'})
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              {/* -------- Billed To -------- */}
              <Grid container spacing={4} sx={{ mb:6 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" sx={{ fontWeight:'bold', mb:2 }}>Billed To</Typography>
                  <Paper variant="outlined" sx={{ p:3, background:'#f8f9fa' }}>
                    <Typography variant="body1">{billedToName}</Typography>
                    <Typography variant="body2" color="textSecondary">{billedToEmail}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* -------- Items table -------- */}
              <TableContainer component={Paper} sx={{ border:'1px solid #e9ecef', overflowX:'auto', mb:6 }}>
                <Table sx={{ minWidth:600 }}>
                  <TableHead sx={{ background:'linear-gradient(45deg,#2b2d42 30%,#4a4e69 90%)' }}>
                    <TableRow>
                      <TableCell sx={{ color:'white' }}>Item</TableCell>
                      <TableCell sx={{ color:'white' }}>Description</TableCell>
                      <TableCell sx={{ color: 'white' }}>Destination</TableCell>
                      <TableCell sx={{ color:'white' }}>Rate</TableCell>
                      <TableCell sx={{ color:'white' }}>Qty</TableCell>
                      <TableCell sx={{ color:'white' }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payouts.map((p) => (
                      <TableRow key={p.id} sx={{ '&:last-child td':{ borderBottom:0 } }}>
                        <TableCell>{p.creator_id}</TableCell>
                        <TableCell>{p.payout_notes ? p.notes : `PO Number ${p.po_number}`}</TableCell>
                        <TableCell>{p.destination ? p.destination : '—'}</TableCell>
                        <TableCell>${safeAmount(p.amount).toFixed(2)}</TableCell>
                        <TableCell>1</TableCell>
                        <TableCell sx={{ fontWeight:'bold' }}>${safeAmount(p.amount).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}

                    {invoice.invoice_items?.length > 0 && invoice.invoice_items.map((it, idx) => (
                      <TableRow key={idx}>
                        <TableCell>Payment to {company.name}</TableCell>
                        <TableCell sx={{ whiteSpace:'pre-wrap' }}>{it.description}</TableCell>
                        <TableCell>${it.amount.toFixed(2)}</TableCell>
                        <TableCell>1</TableCell>
                        <TableCell>${it.amount.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}

                    {payouts.length === 0 && (!invoice.invoice_items || invoice.invoice_items.length === 0) && (
                      <TableRow>
                        <TableCell />
                        <TableCell>Payment to {company.name}</TableCell>
                        <TableCell>${amountDue.toFixed(2)}</TableCell>
                        <TableCell>1</TableCell>
                        <TableCell>${amountDue.toFixed(2)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* -------- Summary -------- */}
              <Grid container justifyContent="flex-end" sx={{ mb:6 }}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p:3, background:'#f8f9fa' }}>
                    <Stack spacing={1}>
                      <SummaryRow label="Subtotal"         value={subtotal} />
                      <SummaryRow label="Amount Paid"       value={amountPaid} />
                      <Divider sx={{ my:1 }} />
                      <SummaryRow label="Total Due" bold value={outstanding} />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              {/* -------- Banking -------- */}
              <Paper variant="outlined" sx={{ mb:6, p:4, backgroundColor:'#f8f9fa' }}>
                <Typography variant="h6" sx={{ fontWeight:'bold', mb:3, color:'#2b2d42' }}>Banking Information</Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} sm={6}>
                    <BankRow k="Bank Name"            v="Prosperity Bank" />
                    <BankRow k="Account Holder"       v="Pangea" />
                    <BankRow k="Account Number"       v="217841714" />
                    <BankRow k="Routing Number"       v="113122655" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <BankRow k="SWIFT Code"           v="PROYUS44" />
                    <BankRow k="Bank Address"         v="2117 N. Fry Road, Katy, TX 77449" />
                    <BankRow k="Beneficiary Address"  v="8724 Cavell Lane, Houston, TX 77055" />
                  </Grid>
                </Grid>
              </Paper>

              {/* -------- Payment CTA -------- */}
              <Paper
                variant="outlined"
                sx={{
                  p:4,
                  background:'linear-gradient(45deg,#2b2d42 30%,#4a4e69 90%)',
                  textAlign:'center',
                  color:'white',
                }}
              >
                <CheckCircle sx={{ fontSize:40, mb:2 }} />
                <Typography variant="h6" sx={{ fontWeight:'bold', mb:2 }}>Secure Payment Options</Typography>
                <Typography variant="body2" sx={{ mb:3 }}>
                  Pay with Credit Card, PayPal, or Bank Transfer
                </Typography>
                {invoice.invoice_link && (
                  <Button
                    variant="contained"
                    color="secondary"
                    href={invoice.invoice_link}
                    startIcon={<Payment />}
                    sx={{
                      px:6,
                      fontWeight:'bold',
                      background:'white',
                      color:'#2b2d42',
                      '&:hover':{ background:'#f8f9fa' },
                    }}
                  >
                    Pay Now
                  </Button>
                )}
              </Paper>
            </Paper>
          </Box>
        </Box>

        <HomeFooter />
      </Box>
    </ThemeProvider>
  );
};

/* ======= little utility sub-components ======= */
const MetaRow = ({ label, value }) => (
  <Stack direction="row" spacing={2}>
    <Typography variant="body2" color="textSecondary">{label}:</Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

const SummaryRow = ({ label, value, bold = false }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" sx={{ fontWeight:bold ? 700 : 400 }}>{label}:</Typography>
    <Typography variant="body2" sx={{ fontWeight:bold ? 700 : 400 }}>${value.toFixed(2)}</Typography>
  </Stack>
);

const BankRow = ({ k, v }) => (
  <Typography variant="body2"><strong>{k}:</strong> {v}</Typography>
);

export default InvoicePage;
